// ============================================================
// EFFECTIVE TALENTS - Single choke point that unions a
// character's own talents with talents granted by their traits
// (TraitDefinition.grants). Own talents always win dedup.
// Pure functions, no React dependency.
// ============================================================
import type { Character, SkillId, TalentInstance } from './types';
import { TRAIT_CATALOG } from './traitCatalog';
import { TALENT_CATALOG } from './talentCatalog';
import { getSkillDef } from './skillCatalog';
import type { SkillBonusEffect, TalentEffect } from './talentEffects';

/**
 * Talents granted by the character's traits (e.g. the Astartes
 * package), deduped against the character's own talents - an
 * explicitly-taken talent always wins over a granted copy.
 * Multiple granting traits never produce duplicate talentIds.
 */
export function getGrantedTalents(character: Character): TalentInstance[] {
  // Tolerate partial characters (test fixtures, Firestore partials)
  const owned = new Set((character.talents ?? []).map((t) => t.talentId));
  const granted: TalentInstance[] = [];
  for (const inst of character.traits ?? []) {
    for (const grant of TRAIT_CATALOG[inst.traitId]?.grants ?? []) {
      if (!owned.has(grant.talentId) && !alreadyGranted(granted, grant)) granted.push({ ...grant });
    }
  }
  return granted;
}

/**
 * Has another trait already granted this?
 *
 * Parameterized grants (e.g. Heightened Senses ×2) may repeat a talentId
 * with distinct parameters - only same-id-same-param dupes are collapsed
 * across traits. Non-parameterized talents dedupe on id alone.
 */
function alreadyGranted(granted: TalentInstance[], grant: TalentInstance): boolean {
  if (granted.some((g) => g.talentId === grant.talentId && g.parameter === grant.parameter)) return true;
  return grant.parameter === undefined && granted.some((g) => g.talentId === grant.talentId);
}

/**
 * The character's full talent list: own talents first, then
 * trait-granted talents. This is what every resolver / UI list
 * should iterate instead of `character.talents`.
 */
export function getEffectiveTalents(character: Character): TalentInstance[] {
  return [...(character.talents ?? []), ...getGrantedTalents(character)];
}

export interface SkillBonusSource {
  label: string;
  value: number;
}

/**
 * Talent-derived bonuses for one catalog skill (the read-only "Talent"
 * column, v6.30.0). Matches `skill_bonus` effects by exact skill id or by
 * the skill's governing characteristic (e.g. Heightened Senses `governing:
 * 'per'` hits Awareness/Scrutiny/Search/Psyniscience). Unconditional
 * effects only. Multiple instances of the SAME talent (Heightened Senses
 * Sight + Hearing) contribute ONE bonus - parameters merge into the label.
 */
export function collectSkillBonuses(character: Character, skillId: SkillId): SkillBonusSource[] {
  const def = getSkillDef(skillId);
  if (!def) return [];
  const perTalent = new Map<string, TalentBonus>();
  for (const inst of getEffectiveTalents(character)) {
    const talent = TALENT_CATALOG[inst.talentId];
    if (!talent) continue;
    const values = talent.effects.filter((e) => bonusApplies(e, skillId, def.governing)).map((e) => e.value);
    for (const value of values) addBonus(perTalent, inst, talent.name, value);
  }
  return [...[...perTalent.values()].map(bonusSource), ...ownBonuses(character, skillId)];
}

/**
 * The character's OWN bonuses to this skill: what is always on and - on an
 * effective sheet - the stances that are up (`engine/roster/stancedSheet`),
 * each named by where it came from. An effect with a condition is left
 * out: only attack bonuses carry one, and those are applied at the attack.
 * Read as plain records: importing the stance model here would be a cycle.
 */
function ownBonuses(character: Character, skillId: SkillId): SkillBonusSource[] {
  return (character.permanentEffects ?? []).flatMap((effect) =>
    effect.type === 'skill_bonus' && effect.skill === skillId && !effect.when && Number.isFinite(effect.value)
      ? [{ label: effect.source ?? 'Always on', value: effect.value }]
      : [],
  );
}

/** The talent's name, with any parameters in brackets, and its bonus. */
function bonusSource(e: TalentBonus): SkillBonusSource {
  return { label: e.name + (e.params.length ? ' (' + e.params.join(', ') + ')' : ''), value: e.value };
}

/** One talent's bonus to a skill, however many of its instances apply. */
interface TalentBonus {
  name: string;
  params: string[];
  value: number;
}

/** An unconditional skill bonus aimed at this skill, by id or by its governing characteristic. */
function bonusApplies(effect: TalentEffect, skillId: SkillId, governing: string): effect is SkillBonusEffect {
  if (effect.type !== 'skill_bonus' || effect.condition) return false;
  return effect.skill === skillId || (effect.governing != null && effect.governing === governing);
}

/** Fold one instance's bonus into its talent's entry: parameters merge, the best value stands. */
function addBonus(perTalent: Map<string, TalentBonus>, inst: TalentInstance, name: string, value: number): void {
  const entry = perTalent.get(inst.talentId);
  if (!entry) {
    perTalent.set(inst.talentId, { name, params: inst.parameter ? [inst.parameter] : [], value });
    return;
  }
  if (inst.parameter) entry.params.push(inst.parameter);
  entry.value = Math.max(entry.value, value);
}
